// Tareas: las mias, las de todo el equipo o las hechas, agrupadas por cuando vencen. Cada fila se abre en su ficha.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { cambiarEstadoTarea, tareasHechas } from '../api/crm';
import type { Tarea } from '../api/types';
import { useSession } from '../auth/session';
import { K, useMiembros, useTareas } from '../data/queries';
import { normalize, shortDate, time } from '../lib/format';
import { Icon } from '../lib/icons';
import { Empty, ErrorBox, Pill, Skeleton } from '../ui/bits';
import { abrirDialogo } from '../ui/forms';
import { ESTADO_TAREA } from '../ui/TareaFicha';
import { textoPlano } from '../ui/Texto';
import { toast } from '../ui/toasts';

type Grupo = 'vencidas' | 'hoy' | 'semana' | 'despues' | 'sinFecha' | 'hechas';
const GRUPOS: [Grupo, string][] = [
  ['vencidas', 'Vencidas'],
  ['hoy', 'Hoy'],
  ['semana', 'Próximos 7 días'],
  ['despues', 'Más adelante'],
  ['sinFecha', 'Sin fecha'],
  ['hechas', 'Hechas'],
];

function grupoDe(t: Tarea, ahora: Date): Grupo {
  if (t.estado === 'DONE') return 'hechas';
  if (!t.vence) return 'sinFecha';
  const v = new Date(t.vence);
  const finHoy = new Date(ahora);
  finHoy.setHours(23, 59, 59, 999);
  if (v < ahora && v.toDateString() !== ahora.toDateString()) return 'vencidas';
  if (v <= finHoy) return 'hoy';
  return v.getTime() - finHoy.getTime() <= 7 * 864e5 ? 'semana' : 'despues';
}

type Ver = 'mias' | 'todas' | 'hechas';

export function Tareas() {
  const { yo } = useSession();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const ver: Ver = sp.get('ver') === 'todas' ? 'todas' : sp.get('ver') === 'hechas' ? 'hechas' : 'mias';
  const pendientes = useTareas();
  const hechas = useQuery({ queryKey: ['tareas-hechas'], queryFn: tareasHechas, enabled: ver === 'hechas', staleTime: 30_000 });
  const miembros = useMiembros();
  const [completando, setCompletando] = useState<string | null>(null);
  const [buscar, setBuscar] = useState('');
  const [responsable, setResponsable] = useState('');

  const nombreDe = useMemo(() => new Map((miembros.data ?? []).map((m) => [m.id, m.nombre])), [miembros.data]);
  const fuente = ver === 'hechas' ? hechas : pendientes;
  const nq = normalize(buscar.trim());
  const ahora = new Date();
  const lista = (fuente.data ?? [])
    .filter((t) => ver !== 'mias' || t.asignadoId === yo.miembroId)
    .filter((t) => ver === 'mias' || !responsable || (responsable === 'nadie' ? !t.asignadoId : t.asignadoId === responsable))
    .filter((t) => !nq || normalize(`${t.titulo} ${t.cuerpo} ${t.persona?.nombre ?? ''}`).includes(nq));
  const porGrupo = new Map<Grupo, Tarea[]>();
  for (const t of lista) {
    const g = grupoDe(t, ahora);
    porGrupo.set(g, [...(porGrupo.get(g) ?? []), t]);
  }
  for (const [g, l] of porGrupo) l.sort((a, b) => (g === 'hechas' ? (b.actualizado ?? '').localeCompare(a.actualizado ?? '') : (a.vence ?? '9').localeCompare(b.vence ?? '9')));

  const completar = async (t: Tarea) => {
    setCompletando(t.id);
    try {
      await cambiarEstadoTarea(t.id, 'DONE');
      void qc.invalidateQueries({ queryKey: K.tareas });
      void qc.invalidateQueries({ queryKey: ['tareas-hechas'] });
      toast('Tarea hecha.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setCompletando(null);
    }
  };

  const subtitulo =
    ver === 'hechas' ? `${lista.length} hechas en los últimos 30 días` : lista.length ? `${lista.length} pendientes${ver === 'todas' ? ' en el equipo' : ' tuyas'}` : 'Nada pendiente';

  return (
    <div className="view" style={{ maxWidth: 980 }}>
      <header className="vhead">
        <div>
          <h1>Tareas</h1>
          <p className="sub">{subtitulo}</p>
        </div>
        <button className="btn primary" onClick={() => abrirDialogo({ tipo: 'tarea' })}>
          <Icon name="plus" />
          Nueva tarea
        </button>
      </header>
      <nav className="subnav" aria-label="Qué tareas ver">
        {(
          [
            ['mias', 'Mías'],
            ['todas', 'De todo el equipo'],
            ['hechas', 'Hechas'],
          ] as [Ver, string][]
        ).map(([v, txt]) => (
          <button key={v} aria-current={ver === v ? 'page' : undefined} onClick={() => setSp(v === 'mias' ? {} : { ver: v }, { replace: true })}>
            {txt}
          </button>
        ))}
      </nav>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label className="field" style={{ flex: '1 1 240px' }}>
          <Icon name="search" />
          <input id="tareas-buscar" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Título, descripción o contacto" aria-label="Buscar en tareas" />
        </label>
        {ver !== 'mias' ? (
          <select id="tareas-responsable" value={responsable} onChange={(e) => setResponsable(e.target.value)} aria-label="Responsable">
            <option value="">Todos los responsables</option>
            <option value="nadie">Sin asignar</option>
            {(miembros.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {fuente.isPending ? (
        <Skeleton rows={6} />
      ) : fuente.error ? (
        <ErrorBox error={fuente.error} onRetry={() => fuente.refetch()} />
      ) : !lista.length ? (
        <section className="box">
          <Empty
            titulo={nq ? 'Sin resultados' : ver === 'hechas' ? 'Nada hecho todavía' : 'Estás al día'}
            texto={
              nq
                ? 'Ninguna tarea coincide con la búsqueda.'
                : ver === 'hechas'
                  ? 'Las tareas que marques como hechas aparecen aquí durante 30 días.'
                  : ver === 'todas'
                    ? 'El equipo no tiene tareas pendientes.'
                    : 'No tienes tareas pendientes. Las que crees desde una llamada o un contacto aparecen aquí.'
            }
            icon="check"
          />
        </section>
      ) : (
        GRUPOS.filter(([g]) => porGrupo.has(g)).map(([g, titulo]) => (
          <section className="box" key={g}>
            <div className="box-h">
              <h2 className="lbl">{titulo}</h2>
              <Pill tone={g === 'vencidas' ? 'bad' : g === 'hoy' ? 'warn' : g === 'hechas' ? 'ok' : undefined}>{porGrupo.get(g)!.length}</Pill>
            </div>
            <div className="rows">
              {porGrupo.get(g)!.map((t) => (
                <div key={t.id} className="row" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto', alignItems: 'start', padding: '8px 2px' }}>
                  {t.estado === 'DONE' ? (
                    <span className="tarea-hecha" aria-label="Hecha">
                      <Icon name="check" />
                    </span>
                  ) : (
                    <button className="btn sm" onClick={() => completar(t)} disabled={completando === t.id} title="Marcar como hecha" aria-label={`Marcar como hecha: ${t.titulo}`}>
                      <Icon name="check" />
                    </button>
                  )}
                  <button className="tarea-abrir" onClick={() => abrirDialogo({ tipo: 'verTarea', id: t.id })} aria-label={`Abrir tarea: ${t.titulo}`}>
                    <span className="tarea-titulo">
                      {t.titulo}
                      {t.estado === 'IN_PROGRESS' ? <Pill tone="acc">{ESTADO_TAREA.IN_PROGRESS}</Pill> : null}
                    </span>
                    <span className="sb" style={{ display: 'block', fontSize: 12.5 }}>
                      {[t.persona?.nombre, ver !== 'mias' ? (nombreDe.get(t.asignadoId ?? '') ?? 'Sin asignar') : null].filter(Boolean).join(' · ')}
                    </span>
                    {t.cuerpo ? (
                      <span className="sb tarea-resumen">{textoPlano(t.cuerpo)}</span>
                    ) : null}
                  </button>
                  <span className="mono sb" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {t.vence ? `${shortDate(t.vence)} ${time(t.vence)}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
